import { createServerFn } from "@tanstack/react-start";
import { getRequestIP } from "@tanstack/react-start/server";
import { z } from "zod";

/**
 * DB-backed brute-force protection for the unauthenticated auth endpoints
 * (sign-in and password reset). Attempts are logged per email + IP in the
 * `auth_rate_limit` table; the security-definer DB functions enforce the
 * thresholds (5 failed sign-ins / 15 min, 5 reset requests / hour).
 *
 * SECURITY: There is intentionally NO public `recordAuthAttempt` endpoint.
 * Exposing attempt-recording as a standalone HTTP POST let any anonymous
 * caller record fake failed logins for a victim's email and trigger the
 * identifier lockout (denial of service). Instead, the credential check
 * itself runs on the server (`verifySignIn`) and the password-reset email is
 * sent from the server (`requestPasswordReset`); only attempts the server
 * actually observed are recorded. The pre-check (`precheckAuthRateLimit`)
 * stays public because it only reads the limiter and must run before sign-in.
 */

const baseSchema = z.object({
  identifier: z.string().trim().toLowerCase().email().max(255),
  action: z.enum(["login", "password_reset"]),
});

type RateLimitResult = { allowed: boolean; retryAfterSeconds: number };

async function checkLimit(
  identifier: string,
  ip: string,
  action: "login" | "password_reset",
): Promise<RateLimitResult> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: result, error } = await supabaseAdmin.rpc("check_auth_rate_limit", {
    _identifier: identifier,
    _ip: ip,
    _action: action,
  });

  // Fail open: never lock out legitimate users if the limiter itself errors.
  if (error || !result) return { allowed: true, retryAfterSeconds: 0 };

  const r = result as { allowed: boolean; retry_after_seconds: number };
  return {
    allowed: r.allowed !== false,
    retryAfterSeconds: r.retry_after_seconds ?? 0,
  };
}

async function recordAttempt(
  identifier: string,
  ip: string,
  action: "login" | "password_reset",
  success: boolean,
): Promise<void> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  await supabaseAdmin.rpc("record_auth_attempt", {
    _identifier: identifier,
    _ip: ip,
    _action: action,
    _success: success,
  });
}

export const precheckAuthRateLimit = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => baseSchema.parse(data))
  .handler(async ({ data }): Promise<RateLimitResult> => {
    const ip = getRequestIP({ xForwardedFor: true }) ?? "";
    return checkLimit(data.identifier, ip, data.action);
  });

const signInSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  password: z.string().min(1).max(1024),
});

type SignInResult =
  | { ok: true; accessToken: string; refreshToken: string }
  | { ok: false; locked?: boolean; retryAfterSeconds?: number; message: string };

/**
 * Server-side credential verification with inline rate limiting. The password
 * is checked here (not on the client), so a failed attempt is only ever
 * recorded when the server genuinely observed wrong credentials — there is no
 * way for an outsider to forge failures and lock a victim out. On success the
 * fresh session tokens are returned for the browser client to adopt.
 */
export const verifySignIn = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => signInSchema.parse(data))
  .handler(async ({ data }): Promise<SignInResult> => {
    const ip = getRequestIP({ xForwardedFor: true }) ?? "";
    const identifier = data.email;

    const gate = await checkLimit(identifier, ip, "login");
    if (!gate.allowed) {
      return {
        ok: false,
        locked: true,
        retryAfterSeconds: gate.retryAfterSeconds,
        message: "Too many sign-in attempts.",
      };
    }

    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );

    const { data: result, error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error || !result.session) {
      await recordAttempt(identifier, ip, "login", false);
      return { ok: false, message: error?.message ?? "Invalid login credentials." };
    }

    await recordAttempt(identifier, ip, "login", true);
    return {
      ok: true,
      accessToken: result.session.access_token,
      refreshToken: result.session.refresh_token,
    };
  });

const resetSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  redirectTo: z.string().url().max(2048),
});

type ResetResult = { locked: boolean; retryAfterSeconds: number };

/**
 * Server-side password-reset request with inline rate limiting. The reset
 * email is dispatched from the server and the attempt is recorded here, so the
 * limiter can only be advanced by a genuine server-originated request rather
 * than a forged client POST. Always reports success to avoid email enumeration.
 */
export const requestPasswordReset = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => resetSchema.parse(data))
  .handler(async ({ data }): Promise<ResetResult> => {
    const ip = getRequestIP({ xForwardedFor: true }) ?? "";
    const identifier = data.email;

    const gate = await checkLimit(identifier, ip, "password_reset");
    if (!gate.allowed) {
      return { locked: true, retryAfterSeconds: gate.retryAfterSeconds };
    }

    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );

    await supabase.auth.resetPasswordForEmail(data.email, {
      redirectTo: data.redirectTo,
    });

    // Count each reset request toward the limit (no success-reset for resets).
    await recordAttempt(identifier, ip, "password_reset", false);
    return { locked: false, retryAfterSeconds: 0 };
  });
